import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  ArrowRight,
  Award,
  BarChart3,
  BookOpen,
  Boxes,
  Calculator,
  CalendarX,
  Check,
  ClipboardCheck,
  Clock,
  Crosshair,
  DoorOpen,
  Eye,
  EyeOff,
  FileCheck,
  Files,
  Globe,
  GraduationCap,
  HeartHandshake,
  Lightbulb,
  ListChecks,
  Magnet,
  MessageSquare,
  MessagesSquare,
  Milestone,
  MonitorX,
  Palette,
  Puzzle,
  Rocket,
  Shuffle,
  Signpost,
  TrendingDown,
  TrendingUp,
  Unlink,
  Unplug,
  UserCog,
  UserSearch,
  Users,
  Workflow,
} from 'lucide-react';
import { ArrowLeftRight } from 'lucide-react';
import { cn } from '@/lib/utils';

const PROBLEM_ICONS = {
  workflow: Workflow,
  eye: Eye,
  'clipboard-check': ClipboardCheck,
  unlink: Unlink,
  files: Files,
  shuffle: Shuffle,
  'trending-up': TrendingUp,
  'bar-chart': BarChart3,
  'user-cog': UserCog,
  puzzle: Puzzle,
  'user-search': UserSearch,
  'file-check': FileCheck,
  'door-open': DoorOpen,
  'graduation-cap': GraduationCap,
  users: Users,
  'heart-handshake': HeartHandshake,
  'message-square': MessageSquare,
  'messages-square': MessagesSquare,
  award: Award,
  milestone: Milestone,
  'eye-off': EyeOff,
  globe: Globe,
  palette: Palette,
  'calendar-x': CalendarX,
  'monitor-x': MonitorX,
  signpost: Signpost,
  magnet: Magnet,
  'arrow-left-right': ArrowLeftRight,
  'book-open': BookOpen,
  'trending-down': TrendingDown,
  lightbulb: Lightbulb,
  'list-checks': ListChecks,
  crosshair: Crosshair,
  unplug: Unplug,
  clock: Clock,
  calculator: Calculator,
  boxes: Boxes,
  rocket: Rocket,
};

export const ServiceProblems = ({ data, accent, accentDark, accentSoft, index, discussHref, testidPrefix, sectionClass = 'bg-white' }) => {
  const [activeId, setActiveId] = useState(data.items[0].id);
  const reduce = useReducedMotion();
  const active = data.items.find((p) => p.id === activeId);
  const ActiveIcon = PROBLEM_ICONS[active.icon];

  return (
    <section aria-labelledby={`${testidPrefix}-problems-heading`} data-testid={`${testidPrefix}-problems-section`} className={`${sectionClass} py-20 sm:py-28`}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center" data-testid={`${testidPrefix}-problems-header`}>
          <p className="flex items-center justify-center gap-3">
            <span className="font-mono text-xs font-medium tracking-widest" style={{ color: accent }}>{index}</span>
            <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: accent }}>{data.eyebrow}</span>
          </p>
          <h2 id={`${testidPrefix}-problems-heading`} className="mt-4 text-balance text-3xl font-extrabold tracking-tight text-navy-900 sm:text-4xl">
            {data.heading}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">{data.intro}</p>
        </div>

        <div className="mt-14 grid gap-3 sm:grid-cols-2 lg:grid-cols-5" role="group" aria-label="Select a problem to see how Amden helps">
          {data.items.map((problem) => {
            const Icon = PROBLEM_ICONS[problem.icon];
            const isActive = problem.id === activeId;
            return (
              <button
                key={problem.id}
                type="button"
                aria-pressed={isActive}
                aria-controls={`${testidPrefix}-problem-detail`}
                data-testid={`${testidPrefix}-problem-card-${problem.id}`}
                onClick={() => setActiveId(problem.id)}
                className={cn(
                  'flex items-start gap-3 rounded-xl border-2 p-4 text-left transition-[border-color,background-color,box-shadow] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900 focus-visible:ring-offset-2',
                  isActive
                    ? 'border-navy-900 bg-navy-50 shadow-card'
                    : 'border-slate-200 bg-white hover:border-navy-300 hover:shadow-sm'
                )}
              >
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors"
                  style={{
                    backgroundColor: isActive ? accent : accentSoft,
                    color: isActive ? '#ffffff' : accentDark,
                  }}
                >
                  <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
                </span>
                <span className="text-sm font-bold leading-snug text-navy-900">{problem.title}</span>
              </button>
            );
          })}
        </div>

        <div id={`${testidPrefix}-problem-detail`} aria-live="polite" className="mt-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={active.id}
              initial={reduce ? false : { opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduce ? undefined : { opacity: 0, y: -10 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`${testidPrefix}-problem-detail-panel`}
              className="rounded-2xl border border-slate-200 bg-mist p-7 sm:p-9"
            >
              <div className="grid gap-6 md:grid-cols-2 md:gap-10">
                <div>
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg text-white" style={{ backgroundColor: accent }}>
                      <ActiveIcon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <h3 className="text-xl font-extrabold tracking-tight text-navy-900">{active.title}</h3>
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-slate-600 sm:text-base">{active.description}</p>
                </div>
                <div className="border-t border-slate-200 pt-6 md:border-l md:border-t-0 md:pl-10 md:pt-0">
                  <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em]" style={{ color: accentDark }}>
                    <Check className="h-4 w-4" aria-hidden="true" />
                    How Amden helps
                  </p>
                  <p className="mt-3 text-sm leading-relaxed text-navy-800 sm:text-base">{active.howWeHelp}</p>
                  <Link
                    to={discussHref}
                    data-testid={`${testidPrefix}-problem-discuss-link`}
                    className="group mt-5 inline-flex items-center gap-2 text-sm font-bold text-navy-900 transition-colors"
                    onMouseEnter={(e) => (e.currentTarget.style.color = accentDark)}
                    onMouseLeave={(e) => (e.currentTarget.style.color = '')}
                  >
                    Discuss this challenge with us
                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
